import { format, parseISO } from "date-fns";
import { money, qtyFmt, timeFmt } from "../lib/api";

const H = {
  sales: ["Time", "Product", "Depot", "Qty", "Price", "Total", "Customer", "Payment", "By"],
  payments: ["Time", "Customer", "Depot", "Amount", "Method", "By"],
  supplierPayments: ["Time", "Supplier", "Depot", "Amount", "Method", "By"],
  itemsSold: ["SKU", "Product", "Qty sold", "Total"],
  stockIn: ["Time", "Product", "Depot", "Qty", "Unit cost", "Total", "Supplier", "Payment", "By"],
  transfers: ["Time", "Product", "From", "To", "Qty", "By"],
  expenses: ["Time", "Category", "Depot", "Note", "Payment", "Amount", "By"],
  breakdown: ["Depot", "Sales", "Purchases", "Expenses", "Net"],
  lowStock: ["SKU", "Product", "Depot", "Qty", "Reorder level"],
};
const byId = (x) => x.id;

export function RTable({ headers, items, cells, rowKey, testid }) {
  return (
    <div className="overflow-x-auto">
      <table className="rpt-table" data-testid={testid}>
        <thead><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {items.length ? items.map((it) => <tr key={rowKey(it)}>{cells(it).map((c, j) => <td key={headers[j]}>{c}</td>)}</tr>)
            : <tr><td colSpan={headers.length} className="!text-slate-400 italic">No records</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h3 className="font-display text-base font-bold uppercase tracking-wide text-slate-900 border-b-2 border-amber-500 inline-block pr-6">{title}</h3>
      {children}
    </section>
  );
}

export function Stat({ label, value, strong, testid, className = "" }) {
  return (
    <div className={`rounded border p-3 ${strong ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"} ${className}`}>
      <div className={`font-mono text-[10px] uppercase tracking-widest ${strong ? "text-amber-400" : "text-slate-500"}`}>{label}</div>
      <div className="mt-1 font-mono text-lg font-bold" data-testid={testid}>{value}</div>
    </div>
  );
}

export function SummaryStats({ s, netLabel = "Net cash today" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      <Stat label="Opening balance" value={money(s.opening)} testid="report-opening" />
      <Stat label={`Total sales (${s.sales_count})`} value={money(s.sales)} testid="report-sales" />
      <Stat label="Cash sales" value={money(s.cash_sales)} testid="report-cash-sales" />
      <Stat label="Credit sales" value={money(s.credit_sales)} testid="report-credit-sales" />
      <Stat label="Credit collected" value={money(s.collections)} testid="report-collections" />
      <Stat label="Stock purchases" value={money(s.purchases)} testid="report-purchases" />
      <Stat label="Bought on credit" value={money(s.credit_purchases)} testid="report-credit-purchases" />
      <Stat label="Paid to suppliers" value={money(s.supplier_payments)} testid="report-supplier-payments" />
      <Stat label="Expenses" value={money(s.expenses)} testid="report-expenses" />
      <Stat label={netLabel} value={money(s.net)} testid="report-net" />
      <Stat label="Closing balance" value={money(s.closing)} strong className="col-span-2 sm:col-span-5" testid="report-closing" />
    </div>
  );
}

export function Signatures() {
  return (
    <footer className="grid grid-cols-1 sm:grid-cols-2 gap-10 pt-10 text-sm">
      <div><div className="border-b border-slate-400 h-8" /><div className="mt-1 text-slate-500">Prepared by (name &amp; signature)</div></div>
      <div><div className="border-b border-slate-400 h-8" /><div className="mt-1 text-slate-500">Verified by owner (signature)</div></div>
    </footer>
  );
}

export function ReportSheet({ r }) {
  const s = r.summary;
  return (
    <div className="print-sheet rise mx-auto max-w-[920px] space-y-6 rounded-lg bg-white p-5 text-slate-900 shadow-2xl sm:p-10" data-testid="daily-report-sheet">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600">MATWIGA ENTERPRISES · Evening closing</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">Daily Activity Report</h2>
        </div>
        <div className="text-sm sm:text-right space-y-0.5">
          <div className="font-semibold" data-testid="report-depot-label">{r.depot_label}</div>
          <div data-testid="report-date">{format(parseISO(r.date), "EEEE, MMMM d, yyyy")}</div>
          <div className="text-xs text-slate-500">Generated by {r.generated_by} at {timeFmt(r.generated_at)}</div>
        </div>
      </header>

      <SummaryStats s={s} />

      <Section title={`Sales · ${qtyFmt(s.units_sold)} units`}>
        <RTable testid="report-sales-table" headers={H.sales} items={r.sales} rowKey={byId}
          cells={(m) => [timeFmt(m.created_at), m.product_name, m.depot_name, qtyFmt(m.qty), money(m.unit_price), money(m.total), m.party || "—", m.payment_method, m.user_name]} />
        {Object.keys(r.by_payment).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1 text-xs">
            {Object.entries(r.by_payment).map(([k, v]) => <span key={k} className="rounded border border-slate-200 px-2 py-1"><b>{k}:</b> {money(v)}</span>)}
          </div>
        )}
      </Section>

      <Section title="Credit payments received">
        <RTable testid="report-payments-table" headers={H.payments} items={r.payments || []} rowKey={byId}
          cells={(p) => [timeFmt(p.created_at), p.customer, p.depot_name, money(p.amount), p.payment_method, p.user_name]} />
      </Section>

      <Section title="Payments made to suppliers">
        <RTable testid="report-supplier-payments-table" headers={H.supplierPayments} items={r.supplier_payments || []} rowKey={byId}
          cells={(p) => [timeFmt(p.created_at), p.supplier, p.depot_name, money(p.amount), p.payment_method, p.user_name]} />
      </Section>

      <Section title="Items sold summary">
        <RTable headers={H.itemsSold} items={r.items_sold} rowKey={(i) => i.sku} cells={(i) => [i.sku, i.name, `${qtyFmt(i.qty)} ${i.unit}`, money(i.total)]} />
      </Section>

      <Section title={`Stock received · ${qtyFmt(s.units_received)} units`}>
        <RTable testid="report-stock-in-table" headers={H.stockIn} items={r.stock_in} rowKey={byId}
          cells={(m) => [timeFmt(m.created_at), m.product_name, m.depot_name, qtyFmt(m.qty), money(m.unit_price), money(m.total), m.party || "—", m.due_date ? `${m.payment_method} (due ${m.due_date})` : m.payment_method, m.user_name]} />
      </Section>

      <Section title="Depot transfers">
        <RTable headers={H.transfers} items={r.transfers} rowKey={byId}
          cells={(m) => [timeFmt(m.created_at), m.product_name, m.depot_name, m.to_depot_name, qtyFmt(m.qty), m.user_name]} />
      </Section>

      <Section title="Expenses">
        <RTable testid="report-expenses-table" headers={H.expenses} items={r.expenses} rowKey={byId}
          cells={(x) => [timeFmt(x.created_at), x.category, x.depot_name, x.note || "—", x.payment_method, money(x.amount), x.user_name]} />
      </Section>

      {r.breakdown.length > 0 && (
        <Section title="Depot breakdown">
          <RTable headers={H.breakdown} items={r.breakdown} rowKey={(b) => b.depot_name}
            cells={(b) => [b.depot_name, money(b.sales), money(b.purchases), money(b.expenses), money(b.net)]} />
        </Section>
      )}

      <Section title="Low stock alerts">
        <RTable headers={H.lowStock} items={r.low_stock} rowKey={(i) => `${i.sku}-${i.depot_name}`}
          cells={(i) => [i.sku, i.name, i.depot_name, `${qtyFmt(i.qty)} ${i.unit}`, qtyFmt(i.reorder_level)]} />
      </Section>

      <Signatures />
    </div>
  );
}
